import React from 'react';
import { translate } from 'react-i18next';
import Card from '../card/Card';
import { FORMATTER_STYLE, getFormatter } from '../../utils/formatter';

class ProjectList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      limit: typeof props.limit === 'number' ? props.limit : 10,
      offset: typeof props.offset === 'number' ? props.offset : 0,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.limit !== this.props.limit || prevProps.offset !== this.props.offset) {
      this.setState({
        limit: typeof this.props.limit === 'number' ? this.props.limit : 10,
        offset: typeof this.props.offset === 'number' ? this.props.offset : 0,
      });
    }
  }

  getPagingVars() {
    const limit = typeof this.props.limit === 'number' ? this.props.limit : this.state.limit;
    const offset = typeof this.props.offset === 'number' ? this.props.offset : this.state.offset;
    return { limit, offset };
  }

  setOffset(newOffset) {
    const { limit } = this.getPagingVars();
    const useServerPaging = typeof this.props.total === 'number';
    const total = useServerPaging ? this.props.total : (this.props.projects || []).length;
    const safeOffset = Math.max(0, Math.min(newOffset, Math.max(0, total - 1)));
    if (typeof this.props.offset !== 'number') {
      this.setState({ offset: safeOffset });
    }
    if (typeof this.props.onPageChange === 'function') {
      this.props.onPageChange({ limit, offset: safeOffset });
    }
  }

  prevPage() {
    const { limit, offset } = this.getPagingVars();
    this.setOffset(offset - limit);
  }

  nextPage() {
    const { limit, offset } = this.getPagingVars();
    this.setOffset(offset + limit);
  }

  setLimit(newLimit) {
    const parsed = parseInt(newLimit, 10);
    const limit = isNaN(parsed) ? 10 : parsed;
    const offset = 0;
    if (typeof this.props.limit !== 'number') {
      this.setState({ limit, offset });
    }
    if (typeof this.props.onPageChange === 'function') {
      this.props.onPageChange({ limit, offset });
    }
  }

  render() {
    const { t, i18n } = this.props;
    const statusMapper = {
      draft: 'status-pending',
      submitted: 'status-passed',
      rejected: 'status-failed',
      approved: 'status-passed',
    };

    const lang = i18n.language.split('-')[0];
    const ca = {
      en: 'gregory',
      th: 'buddhist',
    }[lang];

    const locale = `${i18n.language}-u-ca-${ca}`;
    const l = (th, en) => (lang === 'th' ? th : en);

    const formatNum = getFormatter(FORMATTER_STYLE.floatFractionDigit2);
    const { limit, offset } = this.getPagingVars();
    const allProjects = this.props.projects || [];
    const useServerPaging = typeof this.props.total === 'number';
    const total = useServerPaging ? this.props.total : allProjects.length;
    const pageProjects = useServerPaging ? allProjects : allProjects.slice(offset, offset + limit);
    const start = total === 0 ? 0 : offset + 1;
    const end = Math.min(offset + limit, total);
    const limitOptions = this.props.limitOptions || [10, 20, 50, 100];

    return (
      <Card header={t('total projects')}>
        <div style={{ minHeight: 260 }}>
          <table>
            <thead>
              <tr>
                <th>{l('ชื่อโครงการ', 'Project Name')}</th>
                <th>{l('วันที่สร้าง', 'Create Date')}</th>
                <th>{l('วันที่แก้ไขล่าสุด', 'Modified Date')}</th>
                <th>{l('ประเภท', 'Type')}</th>
                <th>{l('พื้นที่ (ตร.ม.)', 'Area (m²)')}</th>
                <th>{l('EUI ((กิโลวัตต์-ชั่วโมง/ปี)/ตารางเมตร)', 'EUI (kW-h/year/m²)')}</th>
                <th>{l('ผลประหยัด (กิโลวัตต์-ชั่วโมง/ปี)', 'Saving Energy (kW-h/year)')}</th>
                <th>{l('%ผลประหยัด (ร้อยละ)', '% Saving Energy (%)')}</th>
                <th>{l('พลังงานหมุนเวียน (กิโลวัตต์-ชั่วโมง/ปี)', 'Renewable Energy (kW-h/year)')}</th>
                <th>{l('สถานะ', 'Status')}</th>
              </tr>
            </thead>
            <tbody>
              {pageProjects && pageProjects.length > 0
                ? pageProjects.map((project, index) => (
                    <tr key={index}>
                      <td>{project.name}</td>
                      <td>
                        {project.created_date
                          ? new Date(project.created_date).toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day:'2-digit'})
                          : '-'}
                      </td>
                      <td>
                        {project.modified_date
                          ? new Date(project.modified_date).toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day:'2-digit'})
                          : '-'}
                      </td>
                      <td>{project[`building_type_name_${lang}`]}</td>
                      <td>{project.area == null ? '-' : getFormatter(FORMATTER_STYLE.floatFractionDigit2)(project.area)}</td>                      {(() => {
                        const energy = project.evaluation && project.evaluation.whole_building_energy
                          ? project.evaluation.whole_building_energy
                          : null;
                        const area = Number(project.area);
                        const net = energy ? Number(energy.net_EPA) : null;
                        const ref = energy ? Number(energy.ref_EPA) : null;
                        const eui = area > 0 && net != null ? net / area : null;
                        
                        const saving = ref != null && net != null ? (ref - net) : null;
                        
                        const savingPct = ref != null && net != null && net !== 0 ? (1 - (net / ref)) * 100 : null;
                        const re = energy
                          ? ['renewable_energy_HEE', 'renewable_energy_PVE', 'renewable_energy_other']
                              .reduce((sum, key) => {
                                const val = energy[key];
                                return sum + (val != null && !isNaN(Number(val)) ? Number(val) : 0);
                              }, 0)
                          : null;
                        return (
                          <React.Fragment>
                            <td>{eui == null || isNaN(eui) ? 0 : formatNum(eui)}</td>
                            <td>{saving == null || isNaN(saving) ? 0 : formatNum(saving)}</td>
                            <td>{savingPct == null || isNaN(savingPct) ? 0 : formatNum(savingPct)}</td>
                            <td>{re == null || isNaN(re) ? '-' : formatNum(re)}</td>
                          </React.Fragment>
                        );
                      })()}
                      <td>
                        {(() => {
                          const code = (project.status || '').toUpperCase();
                          const cssClass =
                            code === 'DRF'
                              ? 'status-pending'
                              : code === 'SUB'
                                ? 'status-passed'
                                : code === 'REJ'
                                  ? 'status-failed'
                                  : code === 'APP'
                                    ? 'status-passed'
                                    : statusMapper[(project.status || '').toLowerCase()];
                          const label =
                            code === 'DRF'
                              ? l('ร่าง', 'Draft')
                              : code === 'SUB'
                                ? l('เสร็จสิ้น', 'Done')
                                : code === 'REJ'
                                  ? l('ไม่ผ่านการอนุมัติ', 'Rejected')
                                  : code === 'APP'
                                    ? l('ผ่านการอนุมัติแล้ว', 'Approved')
                                    : t((project.status || '').toLowerCase());
                          return (
                            <span className={'project-status ' + cssClass}>
                              {label}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))
                : <tr>
                  <td style={{ textAlign: 'center' }} colSpan={11}>{t('not found')}</td>
                </tr>}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8 }}>
            <div>
              {l('แสดง', 'Showing')} {start}-{end} {l('จาก', 'of')} {total}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: 6 }}>{l('ต่อหน้า', 'per page')}</span>
              <select
                value={limit}
                onChange={e => this.setLimit(e.target.value)}
                style={{ marginRight: 12 }}
              >
                {limitOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <button
                disabled={offset <= 0}
                onClick={() => this.prevPage()}
              >
                {l('ก่อนหน้า', 'Prev')}
              </button>
              <button
                style={{ marginLeft: 8 }}
                disabled={offset + limit >= total}
                onClick={() => this.nextPage()}
              >
                {l('ถัดไป', 'Next')}
              </button>
            </div>
          </div>
        </div>
      </Card>
    );
  }
}

export default translate('translations')(ProjectList);



// WEBPACK FOOTER //
// ./src/components/projectlist/ProjectList.js